import { Search } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import type { MemberListItem } from '@riverpark/types'

type StatusFilter = MemberListItem['status'] | 'all'
type RoleFilter = MemberListItem['role'] | 'all'

interface MembersFiltersProps {
  search: string
  onSearchChange: (value: string) => void
  status: StatusFilter
  onStatusChange: (value: StatusFilter) => void
  role: RoleFilter
  onRoleChange: (value: RoleFilter) => void
  total: number
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded border px-3 py-1.5 font-sans text-xs font-medium transition-colors duration-150',
        active
          ? 'border-ink bg-ink text-cream'
          : 'border-border bg-card text-muted-foreground hover:border-ink/40 hover:text-ink',
      )}
    >
      {children}
    </button>
  )
}

export function MembersFilters({
  search,
  onSearchChange,
  status,
  onStatusChange,
  role,
  onRoleChange,
  total,
}: MembersFiltersProps) {
  return (
    <Card className="overflow-hidden">
      <div className="flex flex-col gap-4 p-4 lg:flex-row lg:items-center">
        <div className="relative min-w-0 flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            type="search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search by name, email or plot number…"
            className="h-10 w-full rounded border border-input bg-card py-2 pl-10 pr-3.5 font-sans text-sm text-ink placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20"
            aria-label="Search members"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <FilterChip active={status === 'all'} onClick={() => onStatusChange('all')}>
            All
          </FilterChip>
          <FilterChip active={status === 'active'} onClick={() => onStatusChange('active')}>
            Active
          </FilterChip>
          <FilterChip active={status === 'inactive'} onClick={() => onStatusChange('inactive')}>
            Inactive
          </FilterChip>
          <FilterChip active={status === 'invited'} onClick={() => onStatusChange('invited')}>
            Invited
          </FilterChip>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <FilterChip active={role === 'all'} onClick={() => onRoleChange('all')}>
            All roles
          </FilterChip>
          <FilterChip active={role === 'member'} onClick={() => onRoleChange('member')}>
            Member
          </FilterChip>
          <FilterChip active={role === 'committee'} onClick={() => onRoleChange('committee')}>
            Committee
          </FilterChip>
          <FilterChip
            active={role === 'estate_manager'}
            onClick={() => onRoleChange('estate_manager')}
          >
            Estate manager
          </FilterChip>
          <FilterChip active={role === 'admin'} onClick={() => onRoleChange('admin')}>
            Admin
          </FilterChip>
        </div>

        <p className="shrink-0 font-sans text-sm text-muted-foreground lg:ml-auto">
          {total} {total === 1 ? 'member' : 'members'}
        </p>
      </div>
    </Card>
  )
}
